import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../app/redux/hooks';
import Price from '../../common/components/price/Price';
import logger from '../../common/logger/AppLogger';
import { TransactionStatus, useCapturePreAuthMutation, useReleasePreAuthMutation } from '../api';
import { useOperatorSession } from '../appState';
import { showErrorDialog } from '../error-dialog/errorDialogSlice';
import EndGameModal from '../order/components/game/EndGameModal';
import { hasOpenGameLineItem, isGameInProgressError } from '../order/components/game/openGameLineItems';
import { clearOrderState, setTransaction, useTransaction } from '../order/orderSlice';

import { useActiveTransactionId } from './tenderSlice';

function PreAuthActionModal() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const operatorSession = useOperatorSession();
  const transaction = useTransaction();
  const activeTransactionId = useActiveTransactionId();

  const [capturePreAuth] = useCapturePreAuthMutation();
  const [releasePreAuth] = useReleasePreAuthMutation();
  const [busy, setBusy] = useState(false);
  const [endGamesForRetry, setEndGamesForRetry] = useState(false);

  const activeTransaction = transaction.find((x) => x.id === activeTransactionId);
  const heldPreAuth = (activeTransaction?.transactionPreAuthorisations ?? []).find((p) => p.status === 'Authorized') ?? null;

  const close = () => navigate('/tender');

  useEffect(() => {
    if (!heldPreAuth) navigate('/tender');
  }, [heldPreAuth, navigate]);

  if (!heldPreAuth || !activeTransaction) return null;

  const hasOpenGame = hasOpenGameLineItem(activeTransaction.transactionLineItems);

  const runCapture = () => {
    if (!operatorSession) return;
    setBusy(true);
    capturePreAuth({ preAuthId: heldPreAuth.id, operatorId: operatorSession.operator.id, amount: null })
      .unwrap()
      .then((tx) => {
        if (tx.status === TransactionStatus.Closed) {
          dispatch(setTransaction([]));
          dispatch(clearOrderState());
          navigate(`/tender/summary/${tx.id}`, { state: tx });
        } else {
          dispatch(setTransaction([...transaction.filter((p) => p.id !== tx.id), tx]));
          close();
        }
      })
      .catch((err) => {
        logger.error(err);
        if (isGameInProgressError(err)) {
          setEndGamesForRetry(true);
          setBusy(false);
          return;
        }
        const message = typeof err?.data === 'string' ? err.data : err?.data?.message ?? 'Failed to capture pre-authorisation';
        dispatch(showErrorDialog({ message, dismissible: true, buttonText: 'Close' }));
        setBusy(false);
      });
  };

  const onCapture = () => {
    if (busy || !operatorSession) return;
    runCapture();
  };

  const onRelease = () => {
    if (busy || !operatorSession) return;
    setBusy(true);
    releasePreAuth({ preAuthId: heldPreAuth.id, operatorId: operatorSession.operator.id })
      .unwrap()
      .then((tx) => {
        dispatch(setTransaction([...transaction.filter((p) => p.id !== tx.id), tx]));
        close();
      })
      .catch((err) => {
        logger.error(err);
        dispatch(showErrorDialog({ message: err?.data ?? 'Failed to release pre-authorisation', dismissible: true, buttonText: 'Close' }));
        setBusy(false);
      });
  };

  if (endGamesForRetry) {
    return (
      <EndGameModal
        transaction={activeTransaction}
        onAllEnded={() => {
          setEndGamesForRetry(false);
          runCapture();
        }}
        onSkip={() => setEndGamesForRetry(false)}
      />
    );
  }

  return (
    <div className="pos-modal-portal">
      <div className="pos-modal-overlay" onClick={close}>
        <div className="pos-modal pos-modal_role_preauth" onClick={(e) => e.stopPropagation()}>
          <div className="pos-modal__content">
            <div className="pos-preauth-modal">
              <h2 className="pos-preauth-modal__title">Pre-Authorisation Held</h2>
              <div className="pos-preauth-modal__amount">
                <Price price={heldPreAuth.amountAuthorized} />
              </div>
              <div className="pos-preauth-modal__meta">
                {heldPreAuth.cardScheme && <span className="pos-preauth-modal__scheme">{heldPreAuth.cardScheme}</span>}
                {heldPreAuth.cardLast4 && <span className="pos-preauth-modal__last4"> ••{heldPreAuth.cardLast4}</span>}
                {heldPreAuth.authCode && <span className="pos-preauth-modal__auth"> auth {heldPreAuth.authCode}</span>}
              </div>
              {hasOpenGame && (
                <div className="pos-preauth-modal__notice">
                  End all open game sessions before using or releasing this pre-authorisation.
                </div>
              )}
              <div className="pos-preauth-modal__actions">
                <button
                  type="button"
                  className="pos-preauth-modal__action pos-preauth-modal__action_role_capture"
                  disabled={busy || hasOpenGame}
                  onClick={onCapture}
                >
                  Use Card
                </button>
                <button
                  type="button"
                  className="pos-preauth-modal__action pos-preauth-modal__action_role_release"
                  disabled={busy || hasOpenGame}
                  onClick={onRelease}
                >
                  Release
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PreAuthActionModal;
